import React from "react";
import {
  Box,
  Collapse,
  Container,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import type { IconType } from "react-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiBarChart2,
  FiBookOpen,
  FiChevronDown,
  FiGrid,
  FiHome,
  FiInfo,
  FiLogIn,
  FiGrid as FiDashboard,
  FiGift,
  FiPhone,
  FiPieChart,
} from "react-icons/fi";
import { ThemeToggle } from "../ThemeToggle";
import { useAuth } from "../../context/AuthContext";
import { useDashboardNavigate } from "../../hooks/useDashboardNavigate";
import { Logo } from "../Logo";
import { MobileTopBar } from "../MobileTopBar";
import { SERVICES_DATA } from "../../data/services";
import { NEWS_CATEGORIES } from "../../data/news";
import { FUND_CATEGORY_CONFIGS } from "../../config/fundCategories";
import {
  ROUTE_PATHS,
  getNewsCategoryPath,
  getServiceDetailPath,
} from "../../config/routes";

const SafeFiDashboard = FiDashboard as React.FC;
const SafeFiLogIn = FiLogIn as React.FC;
const MotionBox = motion(Box);

interface NavChildItem {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href: string;
  icon: IconType;
  isRoute?: boolean;
  isExternal?: boolean;
  children?: NavChildItem[];
  includeAllOption?: boolean;
}

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const goToDashboard = useDashboardNavigate();

  const bgColor = useColorModeValue("white", "dark.100");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const hoverColor = useColorModeValue("brand.400", "brand.300");

  const drawerBg = useColorModeValue("white", "dark.50");
  const drawerBorder = useColorModeValue("gray.100", "gray.700");
  const activeItemBg = useColorModeValue("brand.50", "brand.900");
  const activeItemColor = useColorModeValue("brand.600", "brand.300");
  const inactiveItemColor = useColorModeValue("gray.700", "gray.200");
  const itemHoverBg = useColorModeValue("gray.50", "gray.700");
  const activeIconBg = useColorModeValue("brand.100", "brand.800");
  const inactiveIconBg = useColorModeValue("gray.100", "gray.700");
  const activeIconColor = useColorModeValue("brand.400", "brand.300");
  const inactiveIconColor = useColorModeValue("gray.500", "gray.400");
  const menuBg = useColorModeValue("white", "dark.100");
  const menuHoverBg = useColorModeValue("gray.50", "dark.300");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems: NavItem[] = [
    { label: "خانه", href: "#hero", icon: FiHome },
    {
      label: "خدمات",
      href: "/services",
      icon: FiGrid,
      isRoute: true,
      children: SERVICES_DATA.map((service) => ({
        label: service.shortTitle,
        href: getServiceDetailPath(service.id),
      })),
    },
    {
      label: "صندوق‌ها",
      href: "/funds",
      icon: FiPieChart,
      isRoute: true,
      children: FUND_CATEGORY_CONFIGS.map((category) => ({
        label: category.title,
        href: category.path,
      })),
    },
    {
      label: "اطلاع‌رسانی",
      href: "/news",
      icon: FiBookOpen,
      isRoute: true,
      children: NEWS_CATEGORIES.map((category) => ({
        label: category.title,
        href: getNewsCategoryPath(category.id),
      })),
    },
    {
      label: "سبدگردانی اختصاصی",
      href: ROUTE_PATHS.DEDICATED_PORTFOLIO,
      icon: FiBarChart2,
      isRoute: true,
    },
    {
      label: "باشگاه مشتریان",
      href: ROUTE_PATHS.CUSTOMER_CLUB,
      icon: FiGift,
      isRoute: true,
    },
    // {
    //   label: "آکادمی امید",
    //   href: "https://omidfa.ir/",
    //   icon: FiBookOpen,
    //   isExternal: true,
    // },
    { label: "درباره ما", href: "/about", icon: FiInfo, isRoute: true },
    { label: "تماس با ما", href: "/contact", icon: FiPhone, isRoute: true },
  ];

  const isRouteActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(`${href}/`);

  const isItemActive = (item: NavItem) => {
    if (item.isExternal) {
      return false;
    }

    if (!item.isRoute) {
      return location.pathname === "/";
    }

    if (isRouteActive(item.href)) {
      return true;
    }

    return item.children?.some((child) => isRouteActive(child.href)) ?? false;
  };

  const handleNavClick = (
    href: string,
    isRoute?: boolean,
    isExternal = false,
  ) => {
    if (isExternal) {
      window.open(href, "_blank", "noopener,noreferrer");
      onClose();
      return;
    }

    if (isRoute) {
      navigate(href);
      onClose();
      return;
    }

    if (location.pathname === "/") {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/", { state: { scrollTo: href } });
    }

    onClose();
  };

  const toggleExpanded = (label: string) => {
    setExpandedItems((current) =>
      current.includes(label)
        ? current.filter((item) => item !== label)
        : [...current, label],
    );
  };

  const handleDashboardClick = goToDashboard;

  return (
    <Box
      as="nav"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      bg={isScrolled ? bgColor : "transparent"}
      backdropFilter={isScrolled ? "blur(10px)" : "none"}
      borderBottom={isScrolled ? "1px" : "0"}
      borderColor={borderColor}
      transition="all 0.3s ease"
      boxShadow={isScrolled ? "sm" : "none"}
    >
      <Box display={{ base: "block", md: "none" }}>
        <MobileTopBar onOpen={onOpen} />
      </Box>

      <Container
        maxW="container.xl"
        py={4}
        display={{ base: "none", md: "block" }}
      >
        <Flex justify="space-between" align="center">
          <Box>
            <Logo variant="horizontal" w="170px" to="/" />
          </Box>

          <HStack spacing={1}>
            {navItems.map((item) => {
              const isActive = isItemActive(item);

              if (item.children && item.children.length > 0) {
                return (
                  <Menu key={item.label}>
                    <MenuButton
                      as={Button}
                      variant="ghost"
                      color={isActive ? "brand.400" : textColor}
                      fontWeight={isActive ? "bold" : "medium"}
                      rightIcon={
                        <Icon
                          verticalAlign="middle"
                          as={FiChevronDown as any}
                        />
                      }
                      borderBottom="1px solid"
                      borderColor={isActive ? "brand.400" : "transparent"}
                      borderRadius={0}
                      transition="all 0.2s"
                      _hover={{
                        bg: "transparent",
                        color: hoverColor,
                        borderBottomColor: "brand.400",
                      }}
                    >
                      {item.label}
                    </MenuButton>
                    <MenuList
                      dir="rtl"
                      bg={menuBg}
                      borderColor={borderColor}
                      shadow="xl"
                      borderRadius="2xl"
                      py={2}
                    >
                      {item.href !== "/funds" && (
                        <MenuItem
                          bg="transparent"
                          _hover={{ bg: menuHoverBg }}
                          onClick={() => handleNavClick(item.href, true)}
                        >
                          همه {item.label}
                        </MenuItem>
                      )}
                      {(item.href === "/funds"
                        ? item.children.filter((child) =>
                            child.href.startsWith("/funds/category/"),
                          )
                        : item.children
                      ).map((child) => (
                        <MenuItem
                          key={child.href}
                          bg="transparent"
                          _hover={{ bg: menuHoverBg }}
                          onClick={() => handleNavClick(child.href, true)}
                        >
                          {child.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                );
              }

              return (
                <Button
                  key={item.label}
                  variant="ghost"
                  color={isActive ? "brand.400" : textColor}
                  fontWeight={isActive ? "bold" : "medium"}
                  _hover={{
                    bg: "transparent",
                    color: hoverColor,
                    borderBottomColor: "brand.400",
                  }}
                  onClick={() =>
                    handleNavClick(item.href, item.isRoute, item.isExternal)
                  }
                  size="md"
                  borderBottom="1px solid"
                  borderColor={isActive ? "brand.400" : "transparent"}
                  borderRadius={0}
                  transition="all 0.2s"
                >
                  {item.label}
                </Button>
              );
            })}
          </HStack>

          <HStack spacing={3}>
            <ThemeToggle />
            <Button
              colorScheme="brand"
              size="md"
              onClick={handleDashboardClick}
              minW="120px"
            >
              OMIDEX
            </Button>
          </HStack>
        </Flex>
      </Container>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay backdropFilter="blur(4px)" />
        <DrawerContent dir="rtl" maxW="320px" bg={drawerBg}>
          <DrawerCloseButton left={3} right="auto" top={4} />

          <Flex
            px={5}
            pt={5}
            pb={4}
            align="center"
            borderBottom="1px solid"
            borderColor={drawerBorder}
          >
            <Logo variant="horizontal" w="130px" to="/" />
          </Flex>

          <DrawerBody px={4} py={5}>
            <VStack spacing={1} align="stretch">
              {navItems.map((item, i) => {
                const isActive = isItemActive(item);
                const isExpanded =
                  expandedItems.includes(item.label) || isActive;

                return (
                  <MotionBox
                    key={item.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.05 } as any}
                  >
                    <Box>
                      <Flex
                        align="center"
                        gap={3}
                        px={4}
                        py={3}
                        borderRadius="xl"
                        cursor="pointer"
                        bg={isActive ? activeItemBg : "transparent"}
                        color={isActive ? activeItemColor : inactiveItemColor}
                        _hover={{
                          bg: itemHoverBg,
                          color: activeItemColor,
                        }}
                        transition="all 0.2s"
                        onClick={() =>
                          item.children?.length
                            ? toggleExpanded(item.label)
                            : handleNavClick(
                                item.href,
                                item.isRoute,
                                item.isExternal,
                              )
                        }
                        role="group"
                      >
                        <Flex
                          w="36px"
                          h="36px"
                          borderRadius="lg"
                          align="center"
                          justify="center"
                          bg={isActive ? activeIconBg : inactiveIconBg}
                          flexShrink={0}
                          transition="all 0.2s"
                          _groupHover={{ bg: activeIconBg }}
                        >
                          <Icon
                            verticalAlign="middle"
                            as={item.icon as any}
                            boxSize={4}
                            color={
                              isActive ? activeIconColor : inactiveIconColor
                            }
                            _groupHover={{ color: activeIconColor }}
                            transition="all 0.2s"
                          />
                        </Flex>
                        <Text
                          fontSize="sm"
                          fontWeight={isActive ? "bold" : "medium"}
                        >
                          {item.label}
                        </Text>

                        {item.children?.length ? (
                          <Icon
                            verticalAlign="middle"
                            as={FiChevronDown as any}
                            boxSize={4}
                            mr="auto"
                            transform={
                              isExpanded ? "rotate(180deg)" : "rotate(0deg)"
                            }
                            transition="transform 0.2s"
                          />
                        ) : isActive ? (
                          <Box
                            mr="auto"
                            w="6px"
                            h="6px"
                            borderRadius="full"
                            bg="brand.400"
                          />
                        ) : null}
                      </Flex>

                      {item.children?.length ? (
                        <Collapse in={isExpanded} animateOpacity>
                          <VStack align="stretch" spacing={1} pt={2} px={2}>
                            {item.href !== "/funds" && (
                              <Button
                                variant="ghost"
                                justifyContent="space-between"
                                borderRadius="lg"
                                fontSize="sm"
                                onClick={() => handleNavClick(item.href, true)}
                              >
                                همه {item.label}
                              </Button>
                            )}
                            {(item.href === "/funds"
                              ? item.children.filter((child) =>
                                  child.href.startsWith("/funds/category/"),
                                )
                              : item.children
                            ).map((child) => (
                              <Button
                                key={child.href}
                                variant="ghost"
                                justifyContent="space-between"
                                borderRadius="lg"
                                fontSize="sm"
                                color={
                                  isRouteActive(child.href)
                                    ? activeItemColor
                                    : inactiveItemColor
                                }
                                onClick={() => handleNavClick(child.href, true)}
                              >
                                {child.label}
                              </Button>
                            ))}
                          </VStack>
                        </Collapse>
                      ) : null}
                    </Box>
                  </MotionBox>
                );
              })}

              <Divider my={3} borderColor={drawerBorder} />

              <MotionBox
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.35 } as any}
              >
                <Button
                  colorScheme="brand"
                  w="100%"
                  size="md"
                  borderRadius="xl"
                  leftIcon={
                    isAuthenticated ? <SafeFiDashboard /> : <SafeFiLogIn />
                  }
                  onClick={handleDashboardClick}
                >
                  OMIDEX
                </Button>
              </MotionBox>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};
